import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import clsx from 'clsx'

/**
 * 코칭 콘솔 공용 확인 다이얼로그.
 * SessionControls(강제 진행/하원/결석) 와 LessonCompleteBar([레슨 완료]) 가 공유한다.
 * tone='danger'(기본) = 빨강 확인 버튼(파괴적/경고), 'primary' = 민트 확인 버튼(일반 확정).
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  pending,
  confirmDisabled = false,
  tone = 'danger',
  variant = 'default',
  warningItemsLabel,
  warningItems,
  children,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel: string
  cancelLabel: string
  pending: boolean
  confirmDisabled?: boolean
  tone?: 'danger' | 'primary'
  variant?: 'default' | 'warning'
  warningItemsLabel?: string
  warningItems?: string[]
  children?: ReactNode
  onCancel: () => void
  onConfirm: () => void
}) {
  const titleId = useId()
  const descriptionId = useId()
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    cancelButtonRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) onCancel()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onCancel, open, pending])

  if (!open) return null
  const isWarning = variant === 'warning'

  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        isWarning ? 'bg-slate-950/50 backdrop-blur-[2px]' : 'bg-black/40',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={() => {
        if (!pending) onCancel()
      }}
    >
      <div
        className={clsx(
          'w-full bg-white',
          isWarning
            ? 'max-w-md rounded-3xl p-7 shadow-2xl sm:p-8'
            : 'max-w-sm rounded-xl p-6 shadow-lg',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isWarning ? (
          <div className="mb-6">
            <div className="mb-5 flex size-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
              <AlertTriangle className="size-6" aria-hidden="true" />
            </div>
            <h2 id={titleId} className="text-2xl font-extrabold tracking-tight text-gray-950">
              {title}
            </h2>
            {warningItems && warningItems.length > 0 ? (
              <div className="mt-5 rounded-2xl border border-red-100 bg-red-50 px-5 py-4">
                {warningItemsLabel ? (
                  <p className="text-xs font-bold text-red-600">{warningItemsLabel}</p>
                ) : null}
                <ul className="mt-2 flex flex-wrap gap-2">
                  {warningItems.map((item) => (
                    <li
                      key={item}
                      className="rounded-lg border border-red-100 bg-white px-3 py-2 text-base font-extrabold text-red-700"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <p
              id={descriptionId}
              className="mt-4 whitespace-pre-line text-base font-semibold leading-7 text-gray-700"
            >
              {message}
            </p>
          </div>
        ) : (
          <>
            <h2 id={titleId} className="mb-2 text-lg font-bold text-gray-900">{title}</h2>
            <p
              id={descriptionId}
              className={clsx('whitespace-pre-line text-sm text-gray-700', children ? 'mb-4' : 'mb-5')}
            >
              {message}
            </p>
          </>
        )}
        {children ? <div className="mb-5">{children}</div> : null}
        <div className={isWarning ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
          <button
            ref={cancelButtonRef}
            type="button"
            className={clsx(
              'border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
              isWarning ? 'min-h-12 rounded-xl px-5 text-base' : 'rounded-lg px-4 py-2 text-sm',
            )}
            onClick={onCancel}
            disabled={pending}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={clsx(
              'font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:bg-gray-300',
              isWarning ? 'min-h-12 rounded-xl px-5 text-base' : 'rounded-lg px-4 py-2 text-sm',
              tone === 'primary'
                ? 'bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-600'
                : 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-600',
            )}
            onClick={onConfirm}
            disabled={pending || confirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
